/**
 * The panel's state machine, pure so it is testable:
 *   closed ⇄ open ⇄ minimised (the floating bubble), unchanged by navigation — the page chip just follows along;
 *   Show me: idle → starting → recording → stopping → sending → idle, minimised while recording, open again to show
 *   Pippi's summary. A recording can't be closed away: closing while recording minimises.
 * The open / minimised choice and the tab are kept per browser (localStorage) so a reload brings the bubble back;
 * a recording never survives a reload.
 */
import type { PageInfo } from './pippiApi';

/** Recording caps (the server's file cap is 25 MB too). Here, not in recorder.ts, so the small half can read them. */
export const MAX_RECORD_SECONDS = 90;
export const MAX_RECORD_BYTES = 25 * 1024 * 1024;

export type Mode = 'closed' | 'open' | 'minimised';
export type Tab = 'chat' | 'cases';
export type RecPhase = 'idle' | 'starting' | 'recording' | 'stopping' | 'sending';

export interface UiState {
  mode: Mode;
  tab: Tab;
  rec: RecPhase;
  recSeconds: number;
  /** The page the person is on now ("Now on: …"). */
  nowOn: PageInfo | null;
}

export type UiAction =
  | { type: 'open'; tab?: Tab }
  | { type: 'close' }
  | { type: 'minimise' }
  | { type: 'toggle' }
  | { type: 'tab'; tab: Tab }
  | { type: 'route'; page: PageInfo }
  | { type: 'rec/start' }
  | { type: 'rec/started' }
  | { type: 'rec/tick'; seconds: number }
  | { type: 'rec/stop' }
  | { type: 'rec/sending' }
  | { type: 'rec/done' }
  | { type: 'rec/failed' };

export const initialUi = (persisted?: Partial<Pick<UiState, 'mode' | 'tab'>> | null): UiState => ({
  mode: persisted?.mode === 'open' || persisted?.mode === 'minimised' ? persisted.mode : 'closed',
  tab: persisted?.tab === 'cases' ? 'cases' : 'chat',
  rec: 'idle',
  recSeconds: 0,
  nowOn: null,
});

const recording = (s: UiState) => s.rec !== 'idle';

export function uiReducer(state: UiState, action: UiAction): UiState {
  switch (action.type) {
    case 'open': return { ...state, mode: 'open', tab: action.tab ?? state.tab };
    case 'close': return recording(state) ? { ...state, mode: 'minimised' } : { ...state, mode: 'closed' };
    case 'minimise': return { ...state, mode: 'minimised' };
    case 'toggle': return state.mode === 'open' ? uiReducer(state, { type: recording(state) ? 'minimise' : 'close' }) : { ...state, mode: 'open' };
    case 'tab': return { ...state, tab: action.tab };
    // Navigation never opens, closes or minimises Pippi: she follows along.
    case 'route': return state.nowOn?.path === action.page.path && state.nowOn?.label === action.page.label ? state : { ...state, nowOn: action.page };
    case 'rec/start': return state.rec === 'idle' ? { ...state, rec: 'starting', recSeconds: 0, mode: 'minimised', tab: 'chat' } : state;
    case 'rec/started': return state.rec === 'starting' ? { ...state, rec: 'recording' } : state;
    case 'rec/tick': return state.rec === 'recording' ? { ...state, recSeconds: action.seconds } : state;
    case 'rec/stop': return state.rec === 'recording' || state.rec === 'starting' ? { ...state, rec: 'stopping' } : state;
    case 'rec/sending': return { ...state, rec: 'sending', mode: 'open', tab: 'chat' };
    case 'rec/done': return { ...state, rec: 'idle', recSeconds: 0, mode: 'open', tab: 'chat' };
    case 'rec/failed': return { ...state, rec: 'idle', recSeconds: 0, mode: 'open' };
    default: return state;
  }
}

export const UI_KEY = 'pippi.ui.v1';
type Store = Pick<Storage, 'getItem' | 'setItem'>;

export function loadUi(storage: Store | null | undefined): Partial<Pick<UiState, 'mode' | 'tab'>> | null {
  try { const raw = storage?.getItem(UI_KEY); return raw ? JSON.parse(raw) : null; } catch { return null; }
}
export function saveUi(storage: Store | null | undefined, s: Pick<UiState, 'mode' | 'tab'>): void {
  try { storage?.setItem(UI_KEY, JSON.stringify({ mode: s.mode, tab: s.tab })); } catch { /* private mode: not remembered */ }
}

export const safeStorage = (): Store | null => { try { return window.localStorage; } catch { return null; } };

export const formatSeconds = (s: number) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, '0')}`;
